/* eslint-disable func-names */

import {ShortLocaleNameEnum} from '../../locale/locale-context-type';
import {HelpDeskChatOptionsType} from '../domain-config-type';

import {helpDeskChatSettings, initialChatMessage, usertourSettings} from './init-scripts-const';
import {setDynamicScriptAttributes} from './init-scripts-helper';

type HdeWidgetType = {
    setInitialChatMessage: (message: string) => void;
};

type UsertourType = {
    _stubbed?: boolean;
    load: () => Promise<void>;
    init: (token: string) => void;
    identify: (userId: string, attributes?: Record<string, unknown>) => Promise<void>;
    reset: () => void;
    endAll: () => Promise<void>;
    [method: string]: unknown;
};

type UsertourQueueItemType = [string, {resolve: () => void; reject: (reason?: unknown) => void} | null, Array<unknown>];

declare global {
    interface Window {
        HDE: HdeWidgetType;
        usertour?: UsertourType;
        USERTOURJS_QUEUE?: Array<UsertourQueueItemType>;
        USERTOURJS_ENV_VARS?: Record<string, string>;
    }
}

function loadHelDeskChatScript(url: string, options: HelpDeskChatOptionsType): void {
    const initializedScript: HTMLScriptElement | null = document.querySelector(`#${helpDeskChatSettings.id}`);

    if (initializedScript) {
        setDynamicScriptAttributes(initializedScript, options);

        return;
    }

    const script = document.createElement('script');

    script.type = 'text/javascript';
    script.setAttribute('id', helpDeskChatSettings.id);
    script.dataset.customButton = helpDeskChatSettings.buttonId;
    script.dataset.host = helpDeskChatSettings.host;
    script.dataset.position = helpDeskChatSettings.position;
    setDynamicScriptAttributes(script, options);

    script.addEventListener('load', function () {
        window?.HDE.setInitialChatMessage(initialChatMessage[options?.locale || ShortLocaleNameEnum.ru]);
    });

    script.addEventListener('error', function () {
        console.error('error load helpdeskchat script');
    });

    script.src = url;

    document.querySelectorAll('head')[0]?.append(script);
}

export function initHelpDeskChat(options: HelpDeskChatOptionsType): void {
    loadHelDeskChatScript(helpDeskChatSettings.url, options);
}

// Self-hosted Usertour.js loader. Mirrors the official install stub: it registers a queuing
// `window.usertour` immediately (so init/identify can be called before the bundle arrives) and
// lazily loads the SDK from the self-hosted instance configured in `usertourSettings.envVars`.
function createUsertourStub(): UsertourType {
    window.USERTOURJS_ENV_VARS = usertourSettings.envVars;

    const queue: Array<UsertourQueueItemType> = window.USERTOURJS_QUEUE || [];

    window.USERTOURJS_QUEUE = queue;

    const supportsModules = 'noModule' in HTMLScriptElement.prototype;

    let loadPromise: Promise<void> | null = null;

    function load(): Promise<void> {
        if (loadPromise) {
            return loadPromise;
        }

        loadPromise = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');

            script.async = true;
            script.setAttribute('id', usertourSettings.scriptId);

            if (supportsModules) {
                script.type = 'module';
                script.src = usertourSettings.envVars.USERTOURJS_ES2020_URL;
            } else {
                script.src = usertourSettings.envVars.USERTOURJS_LEGACY_URL;
            }

            script.addEventListener('load', () => resolve());
            script.addEventListener('error', () => {
                loadPromise = null;
                reject(new Error('Could not load Usertour.js'));
            });

            document.querySelectorAll('head')[0]?.append(script);
        });

        return loadPromise;
    }

    const voidMethods = ['init', 'on', 'off', 'reset', 'setBaseZIndex', 'setServerEndpoint', 'setCustomNavigate'];
    const promiseMethods = [
        'identify',
        'identifyAnonymous',
        'updateUser',
        'group',
        'updateGroup',
        'track',
        'start',
        'endAll',
    ];

    const stub: UsertourType = {_stubbed: true, load} as UsertourType;

    voidMethods.forEach((method) => {
        stub[method] = (...args: Array<unknown>): void => {
            stub.load().catch((error: unknown) => console.warn(error));
            queue.push([method, null, args]);
        };
    });

    promiseMethods.forEach((method) => {
        stub[method] = (...args: Array<unknown>): Promise<void> => {
            stub.load().catch((error: unknown) => console.warn(error));

            let deferred: {resolve: () => void; reject: (reason?: unknown) => void} | null = null;
            const promise = new Promise<void>((resolve, reject) => {
                deferred = {resolve, reject};
            });

            queue.push([method, deferred, args]);

            return promise;
        };
    });

    return stub;
}

export function initUsertour(): void {
    if (window.usertour) {
        return;
    }

    window.usertour = createUsertourStub();
    window.usertour.init(usertourSettings.token);
}

export function initHubspot(): void {
    // <script type="text/javascript" id="hs-script-loader" async defer src="//js-eu1.hs-scripts.com/26693921.js"></script>
    const head = document.querySelectorAll('head')[0];

    const script = document.createElement('script');

    script.async = true;
    script.defer = true;
    script.type = 'text/javascript';
    script.src = '//js-eu1.hs-scripts.com/26693921.js';
    script.setAttribute('id', 'hs-script-loader');
    head?.append(script);
}
